import { Injectable, inject, signal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { CompanionChatService } from './companion-chat.service';

interface CompanionSpeechRecognitionEvent extends Event {
  readonly resultIndex: number;
  readonly results: SpeechRecognitionResultList;
}

interface CompanionSpeechRecognitionErrorEvent extends Event {
  readonly error: string;
}

interface CompanionSpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  maxAlternatives: number;
  onresult: ((event: CompanionSpeechRecognitionEvent) => void) | null;
  onerror: ((event: CompanionSpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

/**
 * Shared speech-to-text for the companion sidebar and job prompting modal.
 */
@Injectable({
  providedIn: 'root',
})
export class CompanionVoiceInputService {
  private readonly companionChat = inject(CompanionChatService);
  private readonly translocoService = inject(TranslocoService);

  private readonly VOICE_SILENCE_MS = 2_000;
  private readonly VOICE_MAX_LISTEN_MS = 60_000;
  private readonly waveformBarCount = 5;

  readonly voiceSupported = signal(false);
  readonly isListening = signal(false);
  readonly voiceError = signal(false);
  readonly waveformLevels = signal<number[]>([0.28, 0.55, 0.4, 0.62, 0.35]);

  private speechRecognition: CompanionSpeechRecognition | null = null;
  private voiceBaseText = '';
  private voiceFinalTranscript = '';
  private keepListening = false;
  private hasReceivedSpeech = false;
  private sendAfterListening = false;
  private silenceTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private maxListenTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private waveformAudioContext: AudioContext | null = null;
  private waveformAnalyser: AnalyserNode | null = null;
  private waveformStream: MediaStream | null = null;
  private waveformRafId: number | null = null;
  private onCommit: (() => void) | null = null;

  /**
   * Detects browser speech-recognition support.
   * @returns {void}
   */
  detectSupport(): void {
    this.voiceSupported.set(!!this.getSpeechRecognitionConstructor());
  }

  /**
   * Starts or stops live speech recognition for the shared message input.
   * @param {boolean} canStart Whether listening may start
   * @param {() => void} [onCommit] Called after stop when transcript should be submitted
   * @returns {void}
   */
  toggle(canStart: boolean, onCommit?: () => void): void {
    if (this.isListening()) {
      this.stop(true);
      return;
    }

    if (!canStart) {
      return;
    }

    this.onCommit = onCommit ?? null;
    this.startListening();
  }

  /**
   * Stops recognition without sending (e.g. on destroy / close).
   * @param {boolean} commit When true, keep transcript and optionally auto-submit
   * @returns {void}
   */
  stop(commit = false): void {
    this.keepListening = false;
    this.sendAfterListening = commit;
    this.clearSilenceTimer();
    this.clearMaxListenTimer();

    const recognition = this.speechRecognition;
    if (!recognition) {
      this.finishListening();
      return;
    }

    try {
      if (commit) {
        recognition.stop();
      } else {
        recognition.abort();
      }
    } catch {
      this.finishListening();
    }
  }

  /**
   * @returns {(new () => CompanionSpeechRecognition) | null} Browser constructor if available
   */
  private getSpeechRecognitionConstructor():
    (new () => CompanionSpeechRecognition) | null {
    const speechWindow = window as Window & {
      SpeechRecognition?: new () => CompanionSpeechRecognition;
      webkitSpeechRecognition?: new () => CompanionSpeechRecognition;
    };
    return (
      speechWindow.SpeechRecognition ??
      speechWindow.webkitSpeechRecognition ??
      null
    );
  }

  /**
   * Starts continuous speech recognition into the shared text value.
   * @returns {void}
   */
  private startListening(): void {
    const Recognition = this.getSpeechRecognitionConstructor();
    if (!Recognition) {
      this.voiceSupported.set(false);
      return;
    }

    this.voiceError.set(false);
    this.voiceBaseText = this.companionChat.textValue().trim();
    this.voiceFinalTranscript = '';
    this.keepListening = true;
    this.hasReceivedSpeech = false;

    const recognition = new Recognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    recognition.lang = this.resolveSpeechLanguage();

    recognition.onresult = (event: CompanionSpeechRecognitionEvent) => {
      this.hasReceivedSpeech = true;
      let interim = '';
      let newlyFinal = '';

      for (
        let index = event.resultIndex;
        index < event.results.length;
        index++
      ) {
        const result = event.results[index];
        const transcript = result[0]?.transcript ?? '';
        if (result.isFinal) {
          newlyFinal += transcript;
        } else {
          interim += transcript;
        }
      }

      if (newlyFinal) {
        this.voiceFinalTranscript =
          `${this.voiceFinalTranscript}${newlyFinal}`.replace(/\s+/g, ' ');
      }

      const spoken = `${this.voiceFinalTranscript}${
        interim ? `${this.voiceFinalTranscript ? ' ' : ''}${interim}` : ''
      }`
        .replace(/\s+/g, ' ')
        .trim();
      const combined = this.voiceBaseText
        ? spoken
          ? `${this.voiceBaseText} ${spoken}`
          : this.voiceBaseText
        : spoken;

      this.companionChat.textValue.set(
        combined.slice(0, this.companionChat.MAX_LENGTH_CHARACTER_LIMIT),
      );
      this.resetSilenceTimer();
    };

    recognition.onerror = (event: CompanionSpeechRecognitionErrorEvent) => {
      if (event.error === 'aborted' || event.error === 'no-speech') {
        return;
      }
      if (event.error === 'not-allowed') {
        this.keepListening = false;
        this.voiceError.set(true);
        setTimeout(() => this.voiceError.set(false), 4000);
      }
    };

    recognition.onend = () => {
      if (this.keepListening) {
        try {
          recognition.start();
          if (this.hasReceivedSpeech) {
            this.resetSilenceTimer();
          }
        } catch {
          this.finishListening();
        }
        return;
      }
      this.finishListening();
    };

    this.speechRecognition = recognition;
    this.isListening.set(true);
    void this.startWaveformMonitoring();

    try {
      recognition.start();
    } catch {
      this.finishListening();
      this.voiceError.set(true);
      setTimeout(() => this.voiceError.set(false), 4000);
      return;
    }

    this.maxListenTimeoutId = setTimeout(() => {
      this.stop(true);
    }, this.VOICE_MAX_LISTEN_MS);
  }

  /**
   * Starts mic level visualization while listening.
   * @returns {Promise<void>}
   */
  private async startWaveformMonitoring(): Promise<void> {
    this.stopWaveformMonitoring();

    try {
      this.waveformStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      const AudioContextCtor =
        window.AudioContext ||
        (
          window as Window & {
            webkitAudioContext?: typeof AudioContext;
          }
        ).webkitAudioContext;
      if (!AudioContextCtor) {
        return;
      }

      this.waveformAudioContext = new AudioContextCtor();
      const source = this.waveformAudioContext.createMediaStreamSource(
        this.waveformStream,
      );
      this.waveformAnalyser = this.waveformAudioContext.createAnalyser();
      this.waveformAnalyser.fftSize = 128;
      this.waveformAnalyser.smoothingTimeConstant = 0.7;
      source.connect(this.waveformAnalyser);
      this.tickWaveform();
    } catch {
      // Waveform is visual-only; speech recognition can still work.
    }
  }

  /**
   * Updates waveform bar levels from analyser data.
   * @returns {void}
   */
  private tickWaveform(): void {
    if (!this.waveformAnalyser || !this.isListening()) {
      return;
    }

    const data = new Uint8Array(this.waveformAnalyser.frequencyBinCount);
    this.waveformAnalyser.getByteFrequencyData(data);

    const levels: number[] = [];
    const usableBins = Math.max(
      this.waveformBarCount,
      Math.floor(data.length * 0.45),
    );
    const step = Math.max(1, Math.floor(usableBins / this.waveformBarCount));

    for (let index = 0; index < this.waveformBarCount; index++) {
      const sample = data[index * step] ?? 0;
      levels.push(Math.min(1, Math.max(0.18, sample / 180)));
    }

    this.waveformLevels.set(levels);
    this.waveformRafId = requestAnimationFrame(() => this.tickWaveform());
  }

  /**
   * Tears down waveform monitoring resources.
   * @returns {void}
   */
  private stopWaveformMonitoring(): void {
    if (this.waveformRafId != null) {
      cancelAnimationFrame(this.waveformRafId);
      this.waveformRafId = null;
    }

    this.waveformAnalyser = null;

    if (this.waveformAudioContext) {
      void this.waveformAudioContext.close();
      this.waveformAudioContext = null;
    }

    this.waveformStream?.getTracks().forEach((track) => track.stop());
    this.waveformStream = null;
    this.waveformLevels.set([0.28, 0.55, 0.4, 0.62, 0.35]);
  }

  /**
   * @returns {string} BCP-47 language tag for speech recognition
   */
  private resolveSpeechLanguage(): string {
    const activeLang = this.translocoService.getActiveLang();
    return activeLang?.toLowerCase().startsWith('de') ? 'de-DE' : 'en-US';
  }

  /**
   * Restarts the silence auto-stop timer after speech.
   * @returns {void}
   */
  private resetSilenceTimer(): void {
    this.clearSilenceTimer();
    this.silenceTimeoutId = setTimeout(() => {
      this.stop(true);
    }, this.VOICE_SILENCE_MS);
  }

  /**
   * Clears the silence auto-stop timer.
   * @returns {void}
   */
  private clearSilenceTimer(): void {
    if (this.silenceTimeoutId) {
      clearTimeout(this.silenceTimeoutId);
      this.silenceTimeoutId = null;
    }
  }

  /**
   * Clears the max listen duration timer.
   * @returns {void}
   */
  private clearMaxListenTimer(): void {
    if (this.maxListenTimeoutId) {
      clearTimeout(this.maxListenTimeoutId);
      this.maxListenTimeoutId = null;
    }
  }

  /**
   * Finalizes listening state and optionally submits via onCommit.
   * @returns {void}
   */
  private finishListening(): void {
    const shouldSend = this.sendAfterListening;
    const commitHandler = this.onCommit;
    this.keepListening = false;
    this.sendAfterListening = false;
    this.onCommit = null;
    this.clearSilenceTimer();
    this.clearMaxListenTimer();
    this.stopWaveformMonitoring();
    this.speechRecognition = null;
    this.isListening.set(false);

    const trimmed = this.companionChat
      .textValue()
      .trim()
      .slice(0, this.companionChat.MAX_LENGTH_CHARACTER_LIMIT);
    this.companionChat.textValue.set(trimmed);

    if (shouldSend && trimmed && commitHandler) {
      commitHandler();
    }
  }
}
